"use client"

import { useState } from "react"
import Link from "next/link"
import { formatDistanceToNow } from "date-fns"
import { MapPin, Briefcase, Clock, Calendar, DollarSign, Edit, Trash2, Loader2 } from "lucide-react"
import { useRouter } from "next/navigation"
import { trackEvent } from "@/utils/analytics"

interface JobCardWithActionsProps {
  job: {
    id: string
    title: string
    description: string
    location: string
    trade: string
    budget?: string
    timeline: string
    status: string
    createdAt: string
    postedBy: {
      id: string
      name: string
      userType: string
      location: string
      preferredHomeowner?: boolean
    }
    applicantsCount?: number
  }
}

export default function JobCardWithActions({ job }: JobCardWithActionsProps) {
  const router = useRouter()
  const [isDeleting, setIsDeleting] = useState(false)
  const [error, setError] = useState("")

  const handleDelete = async () => {
    if (!confirm("Are you sure you want to delete this job posting?")) {
      return
    }

    setIsDeleting(true)
    setError("")

    try {
      const response = await fetch(`/api/jobs/${job.id}`, {
        method: "DELETE",
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.message || "Failed to delete job")
      }

      // Track job deletion
      trackEvent("job_deleted", {
        job_id: job.id,
        trade: job.trade,
        status: job.status,
        had_applications: (job.applicantsCount || 0) > 0,
      })

      // Refresh the page to show updated job list
      router.refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete job")
      setIsDeleting(false)
    }
  }

  // Format the date
  const postedDate = job.createdAt ? formatDistanceToNow(new Date(job.createdAt), { addSuffix: true }) : "recently"

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-4">
      <div className="px-4 py-5 sm:px-6">
        <div className="flex justify-between items-start">
          <div>
            <Link href={`/jobs/${job.id}`} className="text-xl font-bold text-blue-600 hover:underline">
              {job.title}
            </Link>
            <div className="mt-1 flex flex-wrap items-center text-sm text-gray-500 gap-3">
              {job.location && (
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  {job.location}
                </div>
              )}
              {job.trade && (
                <div className="flex items-center">
                  <Briefcase className="h-4 w-4 mr-1" />
                  <span className="capitalize">{job.trade}</span>
                </div>
              )}
              {job.timeline && (
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  <span className="capitalize">
                    {job.timeline === "urgent"
                      ? "Urgent"
                      : job.timeline === "week"
                        ? "Within a week"
                        : job.timeline === "month"
                          ? "Within a month"
                          : job.timeline === "future"
                            ? "Future project"
                            : "Flexible"}
                  </span>
                </div>
              )}
              {job.budget && (
                <div className="flex items-center">
                  <DollarSign className="h-4 w-4 mr-1" />
                  {job.budget}
                </div>
              )}
              <div className="flex items-center">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                  {job.status}
                </span>
              </div>
              {job.applicantsCount !== undefined && job.applicantsCount > 0 && (
                <div className="flex items-center">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {job.applicantsCount} {job.applicantsCount === 1 ? "application" : "applications"}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex space-x-2">
            <Link
              href={`/jobs/${job.id}/edit`}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <Edit className="h-4 w-4 mr-1" />
              Edit
            </Link>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <div className="border-t border-gray-200 px-4 py-4 sm:px-6">
        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{job.description}</p>
        <div className="flex justify-between items-center text-xs text-gray-500">
          <div className="flex items-center">
            <Calendar className="h-4 w-4 mr-1" />
            Posted {postedDate}
          </div>
          <Link href={`/jobs/${job.id}`} className="text-blue-600 hover:underline">
            View Details
          </Link>
        </div>
      </div>

      {error && <div className="border-t border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
    </div>
  )
}

